import { Box, Chip, Divider, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import Card from '@/components/wrapped/Card';
import Screen from '@/karehero-app/explore/Screen';
import { TalkInput } from '@/karehero-app/explore/Chat';
import {
  CLAIMS_APP,
  DROP_IN,
} from '@/karehero-app/explore/tell-us-once/TellUsOnce.content';

const STATE_META = {
  confirmed: {
    icon: 'check-circle',
    color: 'custom.limeGreen.800',
    note: 'Confirmed',
  },
  next: { icon: 'play', color: 'custom.coral.600', note: 'Next up' },
  later: {
    icon: 'clock',
    color: 'custom.warmOrange.500',
    note: 'You parked this',
  },
} as const;

// E1 — coming back. The recap names what session one actually covered rather
// than showing a percentage, because the thing being tested is whether the
// app can prove it remembered without making the user re-read everything.
export const E1PickUp = () => {
  const c = DROP_IN.e1;
  return (
    <Screen
      footer={
        <Stack spacing={1.5}>
          <Button ariaLabel={c.cta} isFullWidth>
            {c.cta}
          </Button>
          <Button ariaLabel={c.secondary} variant='link' isFullWidth>
            {c.secondary}
          </Button>
        </Stack>
      }
    >
      <Stack sx={{ pt: 4 }} spacing={3}>
        <Stack spacing={1.5}>
          <Typography variant='h2'>{c.headline}</Typography>
          <Typography variant='body1' color='text.secondary'>
            {c.body}
          </Typography>
        </Stack>

        <Divider />

        <Stack divider={<Divider />}>
          {c.captured.map((item) => {
            const meta = STATE_META[item.state];
            return (
              <Stack
                sx={{ alignItems: 'center', py: 1.5 }}
                key={item.label}
                direction='row'
                spacing={2}
              >
                <Box sx={{ color: meta.color, display: 'flex' }}>
                  <Icon icon={meta.icon as never} fontSize={15} />
                </Box>
                <Typography sx={{ flex: 1, minWidth: 0 }} variant='body2'>
                  {item.label}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {meta.note}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Screen>
  );
};

E1PickUp.displayName = 'E1PickUp';

// E2 — the same problem without the gate. You land on home, the unfinished
// claim is one task among the others, and the orb is docked so talking is
// always available rather than being the thing you were interrupted doing.
// The trade against E1 is proof versus ceremony: this never blocks you, but
// it also never shows you what it remembered until you tap in.
export const E2HomeResume = () => {
  const c = DROP_IN.e2;
  return (
    <Screen footer={<TalkInput typePrompt={c.typePrompt} state='idle' />}>
      <Stack sx={{ pt: 2, pb: 3 }} spacing={3}>
        <Stack spacing={0.5}>
          <Typography variant='body2' color='text.secondary'>
            {c.greeting}
          </Typography>
          <Typography variant='h2'>{c.lead}</Typography>
        </Stack>

        {/* The resume task sits above the claims, not inside them: it is the
            one thing already in flight, and burying it in the list would make
            coming back a hunt. */}
        <Card
          title={c.resume.title}
          variant='compact'
          subtitle={c.resume.detail}
          chip={<Chip size='small' label={c.resume.chip} color='warmOrange' />}
          onClick={() => {}}
        />

        <Divider />

        <Stack spacing={1.5}>
          {CLAIMS_APP.b1.claims.map((claim) => (
            <Card
              key={claim.name}
              title={claim.name}
              variant='compact'
              subtitle={claim.detail}
              chip={<Chip size='small' label={claim.state} />}
              onClick={() => {}}
            />
          ))}
        </Stack>
      </Stack>
    </Screen>
  );
};
E2HomeResume.displayName = 'E2HomeResume';
